import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { ErrorState } from "~/components/ErrorState";
import { PageTransition } from "~/components/PageTransition";
import { StepFlowLayout } from "~/components/layouts/StepFlowLayout";
import { Button } from "~/components/ui/button";

/**
 * Confirmation page behind the mandate reminder email's login link (#1676).
 *
 * The email links here with a single-use better-auth magic-link token in the
 * URL fragment. The fragment never reaches a server, so the link scanners that
 * mail providers run over every emailed URL cannot spend the token: fetching
 * this page is harmless. Only the button turns the token into the real
 * `/auth/magic-link/verify` request, a plain browser navigation that sets the
 * session cookie and redirects to the dashboard with the mandate dialog open.
 *
 * better-auth sends a failed verification back here with `?error=`, and a link
 * without a token lands here too. Both offer the OTP login as the way forward,
 * with the mandate step as its return target: the authenticated guard sends a
 * visitor without a session to `/inloggen` and brings them back afterwards.
 */
export const Route = createFileRoute("/(public)/machtiging-ondertekenen")({
  validateSearch: z.object({ error: z.string().optional() }),
  component: MachtigingOndertekenenRoute,
});

/** Where the login lands: the dashboard with the mandate dialog opened on its first step. */
const MANDATE_STEP_PATH = "/dashboard?machtiging=stap1";

const appUrl = (): string =>
  String(import.meta.env.VITE_APP_URL ?? "").replace(/\/+$/, "");

function readTokenFromHash(): string | null {
  if (typeof window === "undefined") return null;
  const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token");
  return token || null;
}

/** The request the magic-link plugin answers with a session cookie and a redirect. */
function verifyUrl(token: string): string {
  const base = appUrl();
  const query = new URLSearchParams({
    token,
    callbackURL: `${base}${MANDATE_STEP_PATH}`,
    errorCallbackURL: `${base}/machtiging-ondertekenen`,
  });
  return `${base}/auth/magic-link/verify?${query.toString()}`;
}

interface FallbackCopy {
  title: string;
  message: string;
}

const FALLBACK_COPY: Record<string, FallbackCopy> = {
  EXPIRED_TOKEN: {
    title: "Deze link is verlopen",
    message:
      "De link uit je e-mail is verlopen. Log in met je e-mailadres, dan gaan we meteen verder met je machtiging.",
  },
  INVALID_TOKEN: {
    title: "Deze link is niet meer geldig",
    message:
      "De link uit je e-mail is al gebruikt of ongeldig. Log in met je e-mailadres, dan gaan we meteen verder met je machtiging.",
  },
};

const MISSING_TOKEN_COPY: FallbackCopy = {
  title: "Ongeldige link",
  message:
    "We konden geen geldige link vinden. Log in met je e-mailadres om je machtiging te ondertekenen.",
};

const DEFAULT_FALLBACK_COPY: FallbackCopy = {
  title: "Er ging iets mis",
  message:
    "Inloggen via de link is niet gelukt. Log in met je e-mailadres om je machtiging via je dashboard te ondertekenen.",
};

function MachtigingOndertekenenRoute() {
  const { error } = Route.useSearch();
  const navigate = useNavigate();
  // Read once on mount: the fragment is the page's input, not state it changes.
  const [token] = useState(readTokenFromHash);

  // A verify error wins over the fragment: better-auth sends the browser back
  // here without one, and a stale token must not offer the button again.
  const view = error
    ? { kind: "fallback" as const, copy: FALLBACK_COPY[error] ?? DEFAULT_FALLBACK_COPY }
    : token
      ? { kind: "confirm" as const, token }
      : { kind: "fallback" as const, copy: MISSING_TOKEN_COPY };

  return (
    <StepFlowLayout showBack={false}>
      <div className="bg-brand-white mx-4 mb-12 flex w-full max-w-2xl flex-col gap-6 p-12 lg:pb-10">
        {view.kind === "fallback" ? (
          <ErrorState
            message={view.copy.message}
            onRetry={() => navigate({ href: MANDATE_STEP_PATH })}
            retryLabel="Inloggen en verder gaan"
            showContactLink={false}
            title={view.copy.title}
          />
        ) : (
          <PageTransition>
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <h1 className="text-2xl font-medium">Onderteken je machtiging</h1>
              <p className="text-muted-foreground max-w-md text-sm">
                Met de knop hieronder log je in en kom je direct bij je machtiging
                terecht. Het ondertekenen kost je nog geen minuut.
              </p>
              <Button
                className="mt-2"
                onClick={() => window.location.assign(verifyUrl(view.token))}
                size="lg"
                type="button"
              >
                Inloggen en ondertekenen
                <ArrowRight strokeWidth="2.2" />
              </Button>
            </div>
          </PageTransition>
        )}
      </div>
    </StepFlowLayout>
  );
}
